// Chat interface component with real-time messaging
import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  TextField,
  IconButton,
  Typography,
  Avatar,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material';
import {
  Send as SendIcon,
  Person as PersonIcon,
  SmartToy as AssistantIcon,
  Download as DownloadIcon,
  AttachFile as FileIcon,
} from '@mui/icons-material';
import { useChat } from '../../contexts/ChatContext';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { Message } from '../../types';
import MarkdownMessage from '../chat/MarkdownMessage';
import { getApiBaseUrl } from '../../utils/apiUrl';

const ChatInterface: React.FC = () => {
  const [messageText, setMessageText] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [messageQueue, setMessageQueue] = useState<string[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const isProcessingRef = useRef(false);
  
  const { conversationId } = useParams<{ conversationId: string }>();
  const { user } = useAuth();
  const { t } = useTranslation();
  const { 
    currentConversation,
    messages,
    loadConversation,
    sendMessage,
    isLoading,
    isConnected,
    isTyping,
    progress,
    error,
    clearError 
  } = useChat();

  // Load conversation when conversationId changes
  useEffect(() => {
    if (conversationId && conversationId !== currentConversation?.id) {
      loadConversation(conversationId);
    }
  }, [conversationId, currentConversation?.id, loadConversation]);

  // Auto-scroll to bottom when new messages arrive or assistant is responding
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Auto-scroll when assistant starts typing (but not user typing)
  useEffect(() => {
    if (progress && progress.status && progress.status !== 'idle') {
      console.log('📊 Progress status changed:', progress.status, 'for conversation:', progress.conversationId);
      scrollToBottom();
    }
  }, [progress]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Helper functions for progress display
  const getProgressIcon = (status: string) => {
    switch (status) {
      case 'started':
        return '🚀';
      case 'thinking':
        return '🤔';
      case 'queued':
        return '⏰';
      case 'processing':
        return '⚙️';
      case 'tool_execution':
        return '🔧';
      case 'tool_running':
        return '⚡';
      case 'preparing_file':
        return '📁';
      case 'retrieving_csv':
        return '📄';
      case 'retrieving_images':
        return '🖼️';
      case 'csv_ready':
      case 'images_ready':
      case 'completed':
        return '✅';
      case 'failed':
      case 'timeout':
      case 'error':
      case 'cancelled':
        return '❌';
      case 'requires_action':
        return '🔄';
      default:
        return '⏳';
    }
  };

  const getProgressMessage = (status: string) => {
    switch (status) {
      case 'started':
        return t('progress.started');
      case 'thinking':
        return t('progress.thinking');
      case 'processing':
        return t('progress.processing');
      case 'queued':
        return t('progress.queued');
      case 'tool_execution':
        return t('progress.toolExecution');
      case 'tool_running':
        return t('progress.toolRunning');
      case 'preparing_file':
        return t('progress.preparingFile');
      case 'retrieving_csv':
        return t('progress.retrievingCsv', 'Récupération des fichiers CSV...');
      case 'retrieving_images':
        return t('progress.retrievingImages', 'Récupération des images...');
      case 'csv_ready':
        return t('progress.csvReady', 'Fichiers CSV prêts');
      case 'images_ready':
        return t('progress.imagesReady', 'Images prêtes');
      case 'completed':
        return t('progress.completed');
      case 'failed':
        return t('progress.failed');
      case 'timeout':
        return t('progress.timeout');
      case 'error':
        return t('progress.error');
      case 'cancelled':
        return t('progress.cancelled');
      case 'requires_action':
        return t('progress.requiresAction');
      default:
        return t('progress.processing'); // fallback to processing
    }
  };

  // Check if assistant is processing
  const isAssistantProcessing = progress && (
    progress.status === 'thinking' || 
    progress.status === 'tool_running' || 
    progress.status === 'tool_execution' ||
    progress.status === 'preparing_file' ||
    progress.status === 'processing' ||
    progress.status === 'queued' ||
    progress.status === 'retrieving_csv' ||
    progress.status === 'retrieving_images'
  );

  // Update processing ref
  useEffect(() => {
    isProcessingRef.current = isAssistantProcessing || false;
  }, [isAssistantProcessing]);

  // Process message queue when assistant finishes
  useEffect(() => {
    if (!isProcessingRef.current && !isSending && messageQueue.length > 0) {
      const nextMessage = messageQueue[0];
      setMessageQueue(prev => prev.slice(1));
      
      console.log(`📤 Processing queued message (${messageQueue.length - 1} remaining)`);
      
      setIsSending(true);
      sendMessage(nextMessage)
        .then(() => {
          console.log('✅ Queued message sent successfully');
        })
        .catch((error) => {
          console.error('❌ Failed to send queued message:', error);
          // Re-add to queue on error
          setMessageQueue(prev => [nextMessage, ...prev]);
        })
        .finally(() => {
          setIsSending(false);
        });
    }
  }, [isAssistantProcessing, isSending, messageQueue, sendMessage]);

  const handleSendMessage = async () => {
    const text = messageText.trim();
    if (!text || !currentConversation) return;

    setMessageText('');

    // If assistant is processing or we're already sending, queue the message
    if (isAssistantProcessing || isSending) {
      console.log('⏳ Assistant busy, queuing message...');
      setMessageQueue(prev => [...prev, text]);
      return;
    }

    setIsSending(true);

    try {
      await sendMessage(text);
      console.log('✅ Message sent successfully');
    } catch (error) {
      console.error('❌ Failed to send message:', error);
      // Restore message text on error
      setMessageText(text);
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (dateString: string) => {
    try {
      // Backend sends UTC dates without 'Z', so we need to add it
      const dateStr = dateString.endsWith('Z') || 
                      dateString.includes('+') || 
                      (dateString.includes('-') && dateString.lastIndexOf('-') > 10)
        ? dateString
        : dateString + 'Z';
      
      return new Date(dateStr).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
        timeZone: 'America/Montreal',
      });
    } catch (error) {
      console.error('Error formatting time:', error);
      return dateString;
    }
  };

  const MessageBubble: React.FC<{ message: Message }> = ({ message }) => {
    const isUser = message.role === 'user';
    
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: isUser ? 'flex-end' : 'flex-start',
          mb: 3,
          alignItems: 'flex-start',
          gap: 2,
          px: 2,
        }}
      >
        {!isUser && (
          <Avatar 
            sx={{ 
              width: 36, 
              height: 36, 
              bgcolor: 'transparent',
              border: '2px solid',
              borderColor: 'primary.main',
              color: 'primary.main',
            }}
          >
            <AssistantIcon fontSize="small" />
          </Avatar>
        )}
        
        <Box sx={{ 
          maxWidth: isUser ? '85%' : '100%', 
          width: isUser ? 'auto' : '100%',
          minWidth: '120px' 
        }}>
          <Paper
            elevation={0}
            sx={{
              p: isUser ? 2.5 : 2,
              bgcolor: isUser ? 'primary.main' : 'transparent',
              color: isUser ? 'primary.contrastText' : 'text.primary',
              borderRadius: isUser ? 3 : 0,
              wordBreak: 'break-word',
              boxShadow: isUser 
                ? '0 2px 8px rgba(102, 126, 234, 0.25)' 
                : 'none',
              border: isUser ? 'none' : 'none',
              '&:hover': isUser ? {
                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
              } : {},
            }}
          >
            <MarkdownMessage 
              content={message.content}
              stripGraphImages={!!(
                (message.metadata?.image_file_ids?.length ?? 0) > 0 ||
                (message.metadata?.container_files && Object.keys(message.metadata.container_files).length > 0) ||
                message.metadata?.csv_filename?.endsWith('.png') ||
                (message.content?.match(/(?:sandbox:\/mnt\/data\/|attachment:|\/mnt\/data\/)([a-zA-Z0-9_\-]+\.png)/gi)?.length ?? 0) > 0
              )}
              isUser={isUser}
              fileIds={message.metadata?.file_ids || (message.metadata?.file_id ? [message.metadata.file_id] : [])}
              imageFileIds={message.metadata?.image_file_ids || []}
              attachmentFilenames={message.metadata?.attachment_filenames || []}
              conversationId={currentConversation?.id || conversationId}
              csvFileId={message.metadata?.csv_file_id}
            />
            
            {/* Graph/image display - metadata OU extraction depuis le contenu (liens .png) */}
            {(() => {
              const hasMetaImg = (message.metadata?.image_file_ids?.length ?? 0) > 0 ||
                (message.metadata?.container_files && Object.keys(message.metadata.container_files).length > 0) ||
                message.metadata?.csv_filename?.endsWith('.png');
              const pngInContent = message.content?.match(/(?:sandbox:\/mnt\/data\/|attachment:|\/mnt\/data\/)([a-zA-Z0-9_\-]+\.png)/gi);
              const extractedPngs = pngInContent
                ? [...new Set(pngInContent.map((s) => s.replace(/^(?:sandbox:\/mnt\/data\/|attachment:|\/mnt\/data\/)/i, '')))]
                : [];
              return hasMetaImg || extractedPngs.length > 0;
            })() && (
              <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                {/* Source 1: image_file_ids (priorité) */}
                {message.metadata?.image_file_ids
                  ?.filter((id: string) => id && id.startsWith('file-'))
                  .map((imageFileId: string, idx: number) => (
                  <Box key={`img-${imageFileId}-${idx}`} sx={{ mb: 1 }}>
                    <img
                      src={`${getApiBaseUrl()}/api/files/${imageFileId}`}
                      alt={t('files.imageFile')}
                      style={{
                        maxWidth: '100%',
                        maxHeight: '400px',
                        borderRadius: '8px',
                        objectFit: 'contain',
                        border: '1px solid #e0e0e0',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                      }}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        const retry = parseInt(target.dataset.imgRetry || '0', 10);
                        if (retry === 0 && (currentConversation?.id || conversationId)) {
                          target.dataset.imgRetry = '1';
                          const sandboxFn = (message.metadata?.csv_filename && message.metadata.csv_filename.endsWith('.png'))
                            ? message.metadata.csv_filename
                            : (() => {
                                const cf = message.metadata?.container_files as Record<string, { filename?: string }> | undefined;
                                const firstPng = cf && Object.values(cf).find((v) => (v?.filename || '').endsWith('.png'));
                                return firstPng?.filename || 'graph.png';
                              })();
                          target.src = `${getApiBaseUrl()}/api/files/sandbox-image/${encodeURIComponent(sandboxFn)}?conversation_id=${currentConversation?.id || conversationId}`;
                        } else {
                          target.style.display = 'none';
                        }
                      }}
                    />
                  </Box>
                )) || null}
                {/* Source 2: container_files ou csv_filename .png sans image_file_ids (fallback sandbox) */}
                {(!message.metadata?.image_file_ids?.length || message.metadata.image_file_ids.length === 0) &&
                 (message.metadata?.container_files || message.metadata?.csv_filename?.endsWith('.png')) && (
                  (() => {
                    const convId = currentConversation?.id || conversationId;
                    if (!convId) return null;
                    const pngFns: string[] = [];
                    if (message.metadata?.container_files) {
                      for (const v of Object.values(message.metadata.container_files as Record<string, { filename?: string }>)) {
                        const fn = v?.filename;
                        if (fn && (fn.endsWith('.png') || fn.endsWith('.jpg') || fn.endsWith('.jpeg'))) pngFns.push(fn);
                      }
                    }
                    if (message.metadata?.csv_filename?.endsWith('.png') && !pngFns.includes(message.metadata.csv_filename)) {
                      pngFns.push(message.metadata.csv_filename);
                    }
                    if (pngFns.length === 0) pngFns.push('graph.png');
                    return pngFns.map((fn, i) => (
                      <Box key={`sandbox-${fn}-${i}`} sx={{ mb: 1 }}>
                        <img
                          src={`${getApiBaseUrl()}/api/files/sandbox-image/${encodeURIComponent(fn)}?conversation_id=${convId}`}
                          alt={t('files.imageFile')}
                          style={{
                            maxWidth: '100%',
                            maxHeight: '400px',
                            borderRadius: '8px',
                            objectFit: 'contain',
                            border: '1px solid #e0e0e0',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                          }}
                        />
                      </Box>
                    ));
                  })()
                )}
                {/* Source 3: extraction depuis le contenu (liens sandbox:/mnt/data/xxx.png ou attachment:xxx.png) */}
                {(!message.metadata?.image_file_ids?.length || message.metadata.image_file_ids.length === 0) &&
                 !message.metadata?.container_files &&
                 !message.metadata?.csv_filename?.endsWith('.png') &&
                 (() => {
                   const m = message.content?.match(/(?:sandbox:\/mnt\/data\/|attachment:|\/mnt\/data\/)([a-zA-Z0-9_\-]+\.png)/gi);
                   return m ? [...new Set(m.map((s) => s.replace(/^(?:sandbox:\/mnt\/data\/|attachment:|\/mnt\/data\/)/i, '')))] : [];
                 })().length > 0 && (
                  (() => {
                    const convId = currentConversation?.id || conversationId;
                    if (!convId) return null;
                    const pngFns = (message.content?.match(/(?:sandbox:\/mnt\/data\/|attachment:|\/mnt\/data\/)([a-zA-Z0-9_\-]+\.png)/gi) || [])
                      .map((s) => s.replace(/^(?:sandbox:\/mnt\/data\/|attachment:|\/mnt\/data\/)/i, ''))
                      .filter((fn, i, arr) => arr.indexOf(fn) === i);
                    return pngFns.map((fn, i) => (
                      <Box key={`content-${fn}-${i}`} sx={{ mb: 1 }}>
                        <img
                          src={`${getApiBaseUrl()}/api/files/sandbox-image/${encodeURIComponent(fn)}?conversation_id=${convId}`}
                          alt={t('files.imageFile')}
                          style={{
                            maxWidth: '100%',
                            maxHeight: '400px',
                            borderRadius: '8px',
                            objectFit: 'contain',
                            border: '1px solid #e0e0e0',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                          }}
                        />
                      </Box>
                    ));
                  })()
                )}
              </Box>
            )}
            
            {/* File display and download - check multiple sources for file_id */}
            {/* Exclure CSV (section dédiée) ET graphiques (gérés par bloc Graph + CSV/Graph) */}
            {(message.metadata?.file_id || message.metadata?.file_ids?.[0]) && 
             !message.metadata?.csv_file_id && 
             !(message.metadata?.file_ids && message.metadata.file_ids.length > 0 && !message.metadata?.image_file_ids && message.metadata?.file_type !== 'image_file') &&
             !(message.metadata?.image_file_ids?.length || message.metadata?.container_files || /(?:sandbox:\/mnt\/data\/|attachment:|\/mnt\/data\/)([a-zA-Z0-9_\-]+\.png)/i.test(message.content || '')) && (
              <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                {/* Image display for image files */}
                {message.metadata?.file_type === 'image_file' && (
                  <Box sx={{ mb: 1 }}>
                    <img
                      src={`${getApiBaseUrl()}/api/files/${message.metadata.file_id || message.metadata.file_ids?.[0]}`}
                      alt={t('files.imageFile')}
                      style={{
                        maxWidth: '100%',
                        maxHeight: '400px',
                        borderRadius: '8px',
                        objectFit: 'contain',
                        border: '1px solid #e0e0e0',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                      }}
                      onError={(e) => {
                        // If image fails to load, hide it and show error message
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        const errorMsg = document.createElement('div');
                        errorMsg.innerHTML = '❌ Failed to load image';
                        errorMsg.style.color = '#d32f2f';
                        errorMsg.style.fontSize = '0.875rem';
                        if (target.parentNode) {
                          target.parentNode.appendChild(errorMsg);
                        }
                        console.error('❌ Image failed to load:', message.metadata.file_id);
                      }}
                    />
                  </Box>
                )}
                
                {/* Download button and file info */}
                <Tooltip title={t('files.download')}>
                  <IconButton
                    size="small"
                    onClick={() => {
                      // Get file_id from metadata (prioritize direct file_id, then first from file_ids array)
                      const fileId = message.metadata.file_id || message.metadata.file_ids?.[0];
                      // Use GCP backend URL
                      const downloadUrl = `${getApiBaseUrl()}/api/files/${fileId}`;
                      console.log('🔽 Downloading file:', fileId, 'GCP URL:', downloadUrl);
                      
                      try {
                        // Method 1: Direct fetch and download
                        fetch(downloadUrl)
                          .then(response => {
                            if (!response.ok) {
                              throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                            }
                            
                            // Extract filename from Content-Disposition header
                            const contentDisposition = response.headers.get('content-disposition');
                            let filename = `file_${fileId}`;
                            
                            if (contentDisposition) {
                              const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
                              if (filenameMatch && filenameMatch[1]) {
                                filename = filenameMatch[1];
                                // Remove /mnt/data/ prefix if present
                                filename = filename.replace(/^\/mnt\/data\//, '');
                                console.log('📝 Extracted filename from header:', filename);
                              }
                            }
                            
                            // If filename still doesn't have extension, add based on content type
                            if (!filename.includes('.')) {
                              const contentType = response.headers.get('content-type') || '';
                              if (contentType.includes('csv')) filename += '.csv';
                              else if (contentType.includes('json')) filename += '.json';
                              else if (contentType.includes('png')) filename += '.png';
                              else if (contentType.includes('jpeg') || contentType.includes('jpg')) filename += '.jpg';
                              else if (contentType.includes('text')) filename += '.txt';
                            }
                            
                            return response.blob().then(blob => ({ blob, filename }));
                          })
                          .then(({ blob, filename }) => {
                            const url = window.URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.href = url;
                            link.download = filename;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            window.URL.revokeObjectURL(url);
                            console.log('✅ File downloaded successfully as:', filename);
                          })
                          .catch(error => {
                            console.error('❌ Download failed, trying fallback method:', error);
                            // Fallback: Open in new tab
                            window.open(downloadUrl, '_blank');
                          });
                      } catch (error) {
                        console.error('❌ Download error:', error);
                        // Final fallback: Direct window open
                        window.open(downloadUrl, '_blank');
                      }
                    }}
                    sx={{
                      bgcolor: isUser ? 'rgba(255,255,255,0.2)' : 'action.hover',
                      '&:hover': {
                        bgcolor: isUser ? 'rgba(255,255,255,0.3)' : 'action.selected',
                      },
                      mr: 1,
                    }}
                  >
                    {message.metadata?.file_type === 'image_file' ? (
                      <FileIcon fontSize="small" />
                    ) : (
                      <DownloadIcon fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    opacity: 0.8,
                    cursor: 'pointer',
                    '&:hover': { textDecoration: 'underline' }
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const fileId = message.metadata.file_id || message.metadata.file_ids?.[0];
                    // Use API base URL utility
                    const downloadUrl = `${getApiBaseUrl()}/api/files/${fileId}`;
                    console.log('🔽 Text click download:', fileId, 'URL:', downloadUrl);
                    
                    // Use fetch method for consistency with header extraction
                    fetch(downloadUrl, {
                      method: 'GET',
                      credentials: 'include', // Include cookies for CORS
                      headers: {
                        'Accept': '*/*',
                      }
                    })
                      .then(response => {
                        if (!response.ok) {
                          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                        }
                        
                        // Extract filename from Content-Disposition header
                        const contentDisposition = response.headers.get('content-disposition');
                        let filename = `file_${fileId}`;
                        
                        if (contentDisposition) {
                          const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
                          if (filenameMatch && filenameMatch[1]) {
                            filename = filenameMatch[1];
                            // Remove /mnt/data/ prefix if present
                            filename = filename.replace(/^\/mnt\/data\//, '');
                            console.log('📝 Extracted filename from header:', filename);
                          }
                        }
                        
                        // If filename still doesn't have extension, add based on content type
                        if (!filename.includes('.')) {
                          const contentType = response.headers.get('content-type') || '';
                          if (contentType.includes('csv')) filename += '.csv';
                          else if (contentType.includes('json')) filename += '.json';
                          else if (contentType.includes('png')) filename += '.png';
                          else if (contentType.includes('jpeg') || contentType.includes('jpg')) filename += '.jpg';
                          else if (contentType.includes('text')) filename += '.txt';
                        }
                        
                        return response.blob().then(blob => ({ blob, filename }));
                      })
                      .then(({ blob, filename }) => {
                        const url = window.URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = url;
                        link.download = filename;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        window.URL.revokeObjectURL(url);
                        console.log('✅ Text click download successful as:', filename);
                      })
                      .catch(error => {
                        console.error('❌ Text click download failed:', error);
                        // Fallback: Open in new tab
                        window.open(downloadUrl, '_blank');
                      });
                  }}
                >
                  {message.metadata?.file_type === 'image_file' ? (
                    <>🖼️ {t('files.imageFile')} ({(message.metadata.file_id || message.metadata.file_ids?.[0] || '').substring(0, 8)}...)</>
                  ) : (
                    <>📁 {t('files.downloadFile')} ({(message.metadata.file_id || message.metadata.file_ids?.[0] || '').substring(0, 8)}...)</>
                  )}
                </Typography>
              </Box>
            )}
            
            {/* CSV/Graph File Status Indicator - show CSV et/ou graphique */}
            {(message.metadata?.csv_file_id || 
              message.metadata?.csv_filename ||
              message.metadata?.image_file_ids?.length ||
              message.metadata?.container_files ||
              (message.metadata?.file_ids && 
               message.metadata.file_ids.length > 0 && 
               message.metadata?.file_type !== 'image_file') ||
              /(?:sandbox:\/mnt\/data\/|attachment:|\/mnt\/data\/)([a-zA-Z0-9_\-]+\.(?:png|csv))/i.test(message.content || '')) && (
              <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                {(() => {
                  // Extraire les noms .png du contenu (fallback quand metadata incomplet)
                  const pngFromContent = (message.content?.match(/(?:sandbox:\/mnt\/data\/|attachment:|\/mnt\/data\/)([a-zA-Z0-9_\-]+\.png)/gi) || [])
                    .map((s) => s.replace(/^(?:sandbox:\/mnt\/data\/|attachment:|\/mnt\/data\/)/i, ''))
                    .filter((fn, i, arr) => arr.indexOf(fn) === i);
                  const hasGraphMeta = !!(message.metadata?.image_file_ids?.length || message.metadata?.container_files);
                  const graphFilename = message.metadata?.csv_filename?.endsWith('.png') 
                    ? message.metadata.csv_filename 
                    : (message.metadata?.container_files && Object.values(message.metadata.container_files as Record<string, { filename?: string }>).find((v) => (v?.filename || '').endsWith('.png'))?.filename)
                    || pngFromContent[0]
                    || (hasGraphMeta ? 'graph.png' : null);
                  const csvFileId = message.metadata?.csv_file_id 
                    || message.metadata?.image_file_ids?.[0] 
                    || message.metadata?.file_ids?.[0];
                  const isGraphOnly = hasGraphMeta || (pngFromContent.length > 0 && !message.metadata?.csv_file_id);
                  const csvFilename = message.metadata?.csv_filename || (isGraphOnly && graphFilename ? graphFilename : 'selection.csv');
                  const csvStatus = message.metadata?.csv_status || (csvFileId ? 'ready' : undefined);
                  
                  if (csvStatus === 'creating') {
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={16} />
                        <Typography variant="caption" color="text.secondary">
                          📊 Création du fichier CSV en cours... ({csvFilename})
                        </Typography>
                      </Box>
                    );
                  } else if (csvStatus === 'ready' || csvFileId || message.metadata?.csv_filename || graphFilename) {
                    const convId = currentConversation?.id || conversationId;
                    const renderDownload = (fname: string, fid: string | null) => {
                      const sandboxUrl = convId
                        ? `${getApiBaseUrl()}/api/files/sandbox-${fname.endsWith('.png') ? 'image' : 'file'}/${encodeURIComponent(fname)}?conversation_id=${convId}`
                        : null;
                      const dlUrl = fid ? `${getApiBaseUrl()}/api/files/${fid}` : sandboxUrl;
                      const isPng = fname.toLowerCase().endsWith('.png');
                      return (
                        <Box key={fname} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Tooltip title={isPng ? t('files.downloadGraph') : t('files.downloadFile')}>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (!dlUrl) return;
                                fetch(dlUrl, { method: 'GET', credentials: 'include', headers: { 'Accept': '*/*' } })
                                  .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.blob(); })
                                  .then((blob) => {
                                    const url = window.URL.createObjectURL(blob);
                                    const link = document.createElement('a');
                                    link.href = url;
                                    link.download = fname;
                                    document.body.appendChild(link);
                                    link.click();
                                    document.body.removeChild(link);
                                    window.URL.revokeObjectURL(url);
                                  })
                                  .catch(() => dlUrl && window.open(dlUrl, '_blank'));
                              }}
                              sx={{ color: 'primary.main' }}
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Typography
                            variant="caption"
                            sx={{ opacity: 0.8, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (dlUrl) window.open(dlUrl, '_blank'); }}
                          >
                            {isPng ? `📊 ${t('files.downloadGraph')}: ${fname}` : `📊 ${t('files.downloadFile')}: ${fname}`}
                          </Typography>
                        </Box>
                      );
                    };
                    const showGraph = !!graphFilename;
                    const showCsv = !!message.metadata?.csv_file_id || (!!csvFileId && !graphFilename);
                    const csvDisplayName = message.metadata?.csv_filename?.endsWith('.csv')
                      ? message.metadata.csv_filename
                      : 'selection.csv';
                    return (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {showGraph && renderDownload(graphFilename!, message.metadata?.image_file_ids?.[0] || null)}
                        {showCsv && renderDownload(csvDisplayName, message.metadata?.csv_file_id || csvFileId || null)}
                      </Box>
                    );
                  }
                  return null;
                })()}
              </Box>
            )}
            
            {/* Show warning only when sandbox path but no file_id AND no conversation (link would be broken) */}
            {message.content && message.content.includes('sandbox:/mnt/data/') && 
             !message.metadata?.file_id && 
             !message.metadata?.file_ids?.length &&
             !(currentConversation?.id || conversationId) && (
              <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                <Typography variant="caption" color="warning.main">
                  {t('files.fileNotAvailable')}
                </Typography>
              </Box>
            )}
          </Paper>
          <Typography 
            variant="caption" 
            color="text.secondary" 
            sx={{ 
              display: 'block', 
              mt: 0.5, 
              textAlign: isUser ? 'right' : 'left',
              px: 0.5,
            }}
          >
            {formatTime(message.created_at)}
          </Typography>
        </Box>

        {isUser && (
          <Avatar 
            sx={{ 
              width: 36, 
              height: 36, 
              bgcolor: 'transparent',
              border: '2px solid',
              borderColor: 'secondary.main',
              color: 'secondary.main',
              fontWeight: 600,
              fontSize: '0.875rem',
            }}
          >
            {user?.full_name?.[0] || user?.username?.[0] || <PersonIcon fontSize="small" />}
          </Avatar>
        )}
      </Box>
    );
  };

  
  if (!conversationId) {
    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          p: 3,
        }}
      >
        <AssistantIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h5" color="text.secondary" gutterBottom>
          {t('dashboard.welcome')}
        </Typography>
        <Typography variant="body1" color="text.secondary" align="center">
          {t('dashboard.selectConversation')}
        </Typography>
      </Box>
    );
  }

  if (isLoading && !currentConversation) {
    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        bgcolor: 'background.default',
      }}
    >

      {/* Error Display - Enhanced */}
      {error && (
        <Alert 
          severity="error" 
          sx={{ 
            m: 1,
            '& .MuiAlert-message': {
              width: '100%',
            },
          }}
          onClose={clearError}
        >
          <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
            {error}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('chat.error.help', 'Si le problème persiste, essayez de reformuler votre question ou contactez le support.')}
          </Typography>
        </Alert>
      )}

      {/* Messages Area */}
      <Box
        sx={{
          flexGrow: 1,
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.default',
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            bgcolor: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
            borderRadius: '4px',
            '&:hover': {
              bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
            },
          },
        }}
      >
        {!messages || messages.length === 0 ? (
          <Box
            sx={{
              flexGrow: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              px: 3,
            }}
          >
            <AssistantIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {t('chat.startConversation')}
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
              {t('chat.startDescription')}
            </Typography>
            
            {/* Query Suggestions */}
            <Box sx={{ width: '100%', maxWidth: 600 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
                {t('chat.suggestions.title', 'Suggestions de requêtes')}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {[
                  t('chat.suggestions.selection', 'Donne-moi 25 retombées pour [Client]'),
                  t('chat.suggestions.analysis', 'Analyse les retombées récentes pour [Client]'),
                  t('chat.suggestions.visualization', 'Crée un graphique de la distribution par Media Reach pour [Client]'),
                  t('chat.suggestions.filter', 'Sélectionne les retombées avec un sentiment positif pour [Client]'),
                ].map((suggestion, index) => (
                  <Paper
                    key={index}
                    elevation={0}
                    sx={{
                      p: 1.5,
                      cursor: 'pointer',
                      border: '1px solid',
                      borderColor: 'divider',
                      bgcolor: 'background.paper',
                      transition: 'all 0.2s',
                      '&:hover': {
                        bgcolor: 'action.hover',
                        borderColor: 'primary.main',
                        transform: 'translateX(4px)',
                      },
                    }}
                    onClick={() => {
                      setMessageText(suggestion);
                      // Focus on input
                      setTimeout(() => {
                        const input = document.querySelector('textarea[aria-label*="message"]') as HTMLTextAreaElement;
                        input?.focus();
                      }, 100);
                    }}
                  >
                    <Typography variant="body2" color="text.primary">
                      {suggestion}
                    </Typography>
                  </Paper>
                ))}
              </Box>
            </Box>
          </Box>
        ) : (
          <>
            {messages && messages.map((message) => (
              <MessageBubble key={message.id} message={message} />
            ))}
            
            {/* Progress indicator as assistant message bubble */}
            {progress.status && progress.status !== 'idle' && progress.status !== 'completed' && progress.conversationId === currentConversation?.id && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  mb: 1,
                  opacity: 1,
                  transition: 'opacity 0.3s ease-in-out',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', maxWidth: '70%' }}>
                  {/* Assistant Avatar */}
                  <Avatar
                    sx={{
                      bgcolor: 'primary.main', // Changed from secondary to primary (green)
                      color: 'white',
                      width: 32,
                      height: 32,
                      mr: 1,
                      flexShrink: 0,
                    }}
                  >
                    <AssistantIcon fontSize="small" />
                  </Avatar>
                  
                  {/* Progress Message Bubble */}
                  <Paper
                    elevation={2}
                    sx={{
                      px: 2.5,
                      py: 1.5,
                      bgcolor: progress.status === 'tool_running' || progress.status === 'tool_execution' 
                        ? 'info.light' // Light blue for tool execution
                        : 'background.paper',
                      borderRadius: '18px 18px 18px 4px',
                      border: '2px solid',
                      borderColor: progress.status === 'tool_running' || progress.status === 'tool_execution'
                        ? 'info.main' // Blue border for tool execution
                        : 'primary.main', // Green border for thinking
                      animation: 'pulse 2s infinite',
                      '@keyframes pulse': {
                        '0%': { opacity: 0.9 },
                        '50%': { opacity: 1 },
                        '100%': { opacity: 0.9 },
                      },
                      boxShadow: (theme) => `0 4px 12px ${theme.palette.primary.main}66`, // Increased shadow for better visibility
                      minWidth: '200px', // Ensure minimum width for visibility
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Typography
                        component="span"
                        sx={{
                          fontSize: '1.3em',
                          animation: 'bounce 1.5s infinite',
                          '@keyframes bounce': {
                            '0%, 20%, 50%, 80%, 100%': { transform: 'translateY(0)' },
                            '40%': { transform: 'translateY(-3px)' },
                            '60%': { transform: 'translateY(-2px)' },
                          }
                        }}
                      >
                        {getProgressIcon(progress.status)}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.primary',
                          fontWeight: 500,
                          fontFamily: 'Inter, sans-serif',
                        }}
                      >
                        {getProgressMessage(progress.status)}
                      </Typography>
                      {/* Animated loading dots */}
                      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                        <Box
                          sx={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            bgcolor: 'primary.main',
                            animation: 'dot-pulse 1.5s infinite',
                            '@keyframes dot-pulse': {
                              '0%, 80%, 100%': { opacity: 0.3 },
                              '40%': { opacity: 1 },
                            },
                          }}
                        />
                        <Box
                          sx={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            bgcolor: 'primary.main',
                            animation: 'dot-pulse 1.5s infinite 0.2s',
                          }}
                        />
                        <Box
                          sx={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            bgcolor: 'primary.main',
                            animation: 'dot-pulse 1.5s infinite 0.4s',
                          }}
                        />
                      </Box>
                    </Box>
                  </Paper>
                </Box>
              </Box>
            )}
            
            {/* Old typing indicator - disabled since we now use progress bubbles */}
            {false && isTyping && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  mb: 1,
                  alignItems: 'flex-start',
                  gap: 1,
                }}
              >
                <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                  <AssistantIcon fontSize="small" />
                </Avatar>
                <Paper
                  elevation={1}
                  sx={{
                    p: 1.5,
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    borderTopLeftRadius: 0,
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <CircularProgress size={16} />
                    <Typography variant="body2" color="text.secondary">
                      Thinking...
                    </Typography>
                  </Box>
                </Paper>
              </Box>
            )}
            
            <div ref={messagesEndRef} />
          </>
        )}
      </Box>

      {/* Message Input */}
      <Box 
        sx={{ 
          p: 3, 
          borderTop: '1px solid',
          borderColor: 'grey.200',
          bgcolor: 'background.paper',
        }}
      >
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', maxWidth: '100%' }}>
          <TextField
            fullWidth
            multiline
            maxRows={6}
            placeholder={isConnected ? t('chat.messagePlaceholder') : t('chat.connectingPlaceholder')}
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={!isConnected || isSending}
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                fontSize: '1rem',
                lineHeight: 1.5,
                minHeight: '48px',
                alignItems: 'flex-end',
                bgcolor: 'background.paper',
                border: '2px solid',
                borderColor: 'grey.200',
                '&:hover': {
                  borderColor: 'primary.light',
                },
                '&.Mui-focused': {
                  borderColor: 'primary.main',
                  boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)',
                },
                '& fieldset': {
                  border: 'none',
                },
              },
              '& .MuiInputBase-input': {
                padding: '12px 16px',
                '&::placeholder': {
                  color: 'text.secondary',
                  opacity: 0.7,
                },
              },
            }}
          />
          <Box sx={{ position: 'relative' }}>
            <Tooltip title={
              !isConnected ? t('chat.disconnected') : 
              isAssistantProcessing ? 'Assistant en traitement... (message sera envoyé après)' :
              messageQueue.length > 0 ? `${messageQueue.length} message(s) en file d'attente` :
              t('chat.send')
            }>
              <span>
                <IconButton
                  onClick={handleSendMessage}
                  disabled={!messageText.trim() || !isConnected}
                  sx={{
                    width: 48,
                    height: 48,
                    bgcolor: messageText.trim() && isConnected 
                      ? (isAssistantProcessing ? 'warning.main' : 'primary.main')
                      : 'grey.200',
                    color: messageText.trim() && isConnected 
                      ? 'white' 
                      : 'grey.500',
                    border: '2px solid transparent',
                    borderRadius: 3,
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      bgcolor: messageText.trim() && isConnected 
                        ? (isAssistantProcessing ? 'warning.dark' : 'primary.dark')
                        : 'action.hover',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    },
                    '&:disabled': {
                      bgcolor: 'action.disabledBackground',
                      color: 'action.disabled',
                      transform: 'none',
                      boxShadow: 'none',
                    },
                  }}
                >
                  {isSending ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <SendIcon />
                  )}
                </IconButton>
              </span>
            </Tooltip>
            {messageQueue.length > 0 && (
              <Box
                sx={{
                  position: 'absolute',
                  top: -8,
                  right: -8,
                  bgcolor: 'warning.main',
                  color: 'white',
                  borderRadius: '50%',
                  width: 20,
                  height: 20,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.75rem',
                  fontWeight: 'bold',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                }}
              >
                {messageQueue.length}
              </Box>
            )}
          </Box>
        </Box>
        
        {/* Helper text */}
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          {!isConnected ? t('chat.connecting') : t('chat.keyboardHint')}
        </Typography>
      </Box>
    </Box>
  );
};

export default ChatInterface;
